import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Users, Save, RefreshCw, ShieldCheck, Shield, FolderOpen, Plus, Trash2, X, Pencil, TrendingUp, Globe, Crown, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Project modal state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    amount: '',
    status: 'active'
  });
  const [savingProject, setSavingProject] = useState(false);

  // Project Catalog state
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogProjects, setCatalogProjects] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogForm, setCatalogForm] = useState({ name: '', description: '', roiYearly: '', published: true });
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [applications, setApplications] = useState([]);
  const [showApplications, setShowApplications] = useState(false);

  // Pending accounts
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // Wallet transactions
  const [walletTxs, setWalletTxs] = useState([]);
  const [loadingWalletTxs, setLoadingWalletTxs] = useState(false);
  const [processingTxId, setProcessingTxId] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      toast({ title: 'Access Denied', description: 'Admin access required', variant: 'destructive' });
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchPendingAccounts();
    fetchWalletTxs();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      const data = {};
      response.data.forEach(u => {
        data[u.id] = {
          balance: u.balance || 0,
          activeInvestments: u.activeInvestments || 0,
          totalGain: u.totalGain || 0
        };
      });
      setEditedData(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingAccounts = async () => {
    setLoadingPending(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/admin/users/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingAccounts(resp.data);
    } catch (e) { console.error('Pending accounts fetch error:', e); }
    setLoadingPending(false);
  };

  const handleAccountStatus = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/account-status`, { accountStatus: status }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `Account ${status}`, description: status === 'approved' ? 'User can now log in.' : 'User access has been rejected.' });
      fetchPendingAccounts();
      fetchUsers();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const fetchWalletTxs = async () => {
    setLoadingWalletTxs(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/admin/wallet/transactions`, { headers: { Authorization: `Bearer ${token}` } });
      setWalletTxs(resp.data);
    } catch (e) { console.error('Wallet txs fetch error:', e); }
    setLoadingWalletTxs(false);
  };

  const handleWalletTxAction = async (txId, action) => {
    setProcessingTxId(txId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/wallet/transactions/${txId}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `Transaction ${action}d`, description: `The transaction has been ${action}d successfully.` });
      fetchWalletTxs();
      fetchUsers();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    setProcessingTxId(null);
  };

  const handleFieldChange = (userId, field, value) => {
    setEditedData({
      ...editedData,
      [userId]: {
        ...editedData[userId],
        [field]: parseFloat(value) || 0
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updates = [];

      users.forEach(u => {
        const edited = editedData[u.id];
        if (edited.balance !== u.balance || 
            edited.activeInvestments !== u.activeInvestments || 
            edited.totalGain !== u.totalGain) {
          updates.push({
            userId: u.id,
            balance: edited.balance,
            activeInvestments: edited.activeInvestments,
            totalGain: edited.totalGain
          });
        }
      });

      if (updates.length === 0) {
        toast({ title: 'No Changes', description: 'No changes to save' });
        setSaving(false);
        return;
      }

      for (const update of updates) {
        await axios.post(`${API}/admin/update-balance`, update, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast({ title: 'Success', description: `Updated ${updates.length} user(s)` });
      await fetchUsers();
    } catch (error) {
      console.error('Error saving data:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save changes',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const openProjectModal = async (u) => {
    setSelectedUser(u);
    setShowProjectModal(true);
    setProjectForm({ name: '', description: '', amount: '', status: 'active' });
    await fetchUserProjects(u.id);
  };

  const fetchUserProjects = async (userId) => {
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/projects/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProjects(response.data);
    } catch (error) {
      console.error('Error fetching user projects:', error);
      setUserProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleAddProject = async () => {
    if (!projectForm.name.trim()) {
      toast({ title: 'Error', description: 'Project name is required', variant: 'destructive' });
      return;
    }
    setSavingProject(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/projects`, {
        userId: selectedUser.id,
        name: projectForm.name.trim(),
        description: projectForm.description.trim(),
        amount: parseFloat(projectForm.amount) || 0,
        status: projectForm.status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: `Project added for ${selectedUser.firstName}` });
      setProjectForm({ name: '', description: '', amount: '', status: 'active' });
      await fetchUserProjects(selectedUser.id);
    } catch (error) {
      console.error('Error adding project:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add project',
        variant: 'destructive',
      });
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted', description: 'Project removed' });
      await fetchUserProjects(selectedUser.id);
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({ title: 'Error', description: 'Failed to delete project', variant: 'destructive' });
    }
  };

  // Catalog management
  const fetchCatalogProjects = async () => {
    setLoadingCatalog(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/catalog/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalogProjects(response.data);
    } catch {
      console.error('Error fetching catalog');
    } finally {
      setLoadingCatalog(false);
    }
  };

  const openCatalogModal = () => {
    setShowCatalogModal(true);
    setCatalogForm({ name: '', description: '', roiYearly: '', published: true });
    setEditingCatalog(null);
    fetchCatalogProjects();
  };

  const handleSaveCatalog = async () => {
    if (!catalogForm.name.trim()) {
      toast({ title: 'Error', description: 'Project name is required', variant: 'destructive' });
      return;
    }
    setSavingCatalog(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: catalogForm.name.trim(),
        description: catalogForm.description.trim(),
        roiYearly: parseFloat(catalogForm.roiYearly) || 0,
        published: catalogForm.published
      };
      if (editingCatalog) {
        await axios.put(`${API}/admin/catalog/projects/${editingCatalog}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Updated', description: 'Project updated successfully' });
      } else {
        await axios.post(`${API}/admin/catalog/projects`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Created', description: 'Project added to catalog' });
      }
      setCatalogForm({ name: '', description: '', roiYearly: '', published: true });
      setEditingCatalog(null);
      await fetchCatalogProjects();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSavingCatalog(false);
    }
  };

  const handleEditCatalog = (project) => {
    setEditingCatalog(project.id);
    setCatalogForm({
      name: project.name,
      description: project.description || '',
      roiYearly: project.roiYearly?.toString() || '',
      published: project.published !== false
    });
  };

  const handleDeleteCatalog = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/catalog/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted', description: 'Catalog project removed' });
      await fetchCatalogProjects();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const fetchApplications = async () => {
    setShowApplications(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch {
      console.error('Error fetching applications');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'paused': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0d6571] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0d6571] shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:text-white/80"
              >
                <ArrowLeft className="mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-white/30"></div>
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-white" />
                <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white text-sm">
                Admin: <strong>{user?.username}</strong>
              </span>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-2 border-white text-white rounded-full px-6 hover:bg-white hover:text-[#0d6571]"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Management Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/news')}
            data-testid="admin-news-link"
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-left"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">News Management</h3>
            <p className="text-gray-600 text-sm">Create and manage news articles for the homepage</p>
          </button>
          <button
            onClick={() => navigate('/admin/creations')}
            data-testid="admin-creations-link"
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-left"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Our Creations</h3>
            <p className="text-gray-600 text-sm">Manage showcase items and case studies</p>
          </button>
          <button
            onClick={openCatalogModal}
            data-testid="admin-catalog-link"
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-left border-2 border-amber-200"
          >
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold text-gray-900">Project Catalog</h3>
            </div>
            <p className="text-gray-600 text-sm">Add/edit discoverable projects with ROI</p>
          </button>
          <button
            onClick={fetchApplications}
            data-testid="admin-applications-link"
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-left border-2 border-[#0d6571]/20"
          >
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="w-5 h-5 text-[#0d6571]" />
              <h3 className="text-lg font-bold text-gray-900">Applications</h3>
            </div>
            <p className="text-gray-600 text-sm">View user project applications</p>
          </button>
        </div>

        {/* Pending Account Approvals */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="pending-accounts-section">
          <div className="px-6 py-4 border-b flex items-center justify-between bg-amber-50">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold text-gray-900">Pending Account Approvals</h3>
              {pendingAccounts.length > 0 && <span className="text-xs font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">{pendingAccounts.length}</span>}
            </div>
            <Button onClick={fetchPendingAccounts} variant="outline" size="sm" className="rounded-full border-2 border-gray-200">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingPending ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {loadingPending ? (
            <div className="p-12 text-center text-gray-400 text-sm">Loading...</div>
          ) : pendingAccounts.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm" data-testid="no-pending-accounts">No pending accounts</div>
          ) : (
            <div className="divide-y">
              {pendingAccounts.map(acc => (
                <div key={acc.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition" data-testid={`pending-account-${acc.id}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{acc.firstName} {acc.lastName}</p>
                    <p className="text-sm text-gray-500">{acc.email} &middot; @{acc.username}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Registered: {acc.createdAt ? new Date(acc.createdAt).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button onClick={() => handleAccountStatus(acc.id, 'approved')} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 h-8 text-xs" data-testid={`approve-account-${acc.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button onClick={() => handleAccountStatus(acc.id, 'rejected')} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-full px-4 h-8 text-xs" data-testid={`reject-account-${acc.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Wallet Transactions */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="admin-wallet-txs-section">
          <div className="px-6 py-4 border-b flex items-center justify-between bg-blue-50">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Wallet Transactions</h3>
              {walletTxs.filter(t => t.status === 'pending_approval').length > 0 && (
                <span className="text-xs font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full">{walletTxs.filter(t => t.status === 'pending_approval').length} pending</span>
              )}
            </div>
            <Button onClick={fetchWalletTxs} variant="outline" size="sm" className="rounded-full border-2 border-gray-200" data-testid="refresh-admin-wallet-txs">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingWalletTxs ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {loadingWalletTxs ? (
            <div className="p-12 text-center text-gray-400 text-sm">Loading...</div>
          ) : walletTxs.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm">No wallet transactions</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-wallet-txs-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">User</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Amount</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Details</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Date</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {walletTxs.map(tx => (
                    <tr key={tx.id} className={`border-b border-gray-50 ${tx.status === 'pending_approval' ? 'bg-amber-50/40' : ''}`} data-testid={`admin-tx-${tx.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{tx.username}</p>
                        <p className="text-xs text-gray-400">{tx.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${tx.type === 'deposit' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                          {tx.type === 'deposit' ? '↓' : '↑'} {tx.type}
                        </span>
                      </td>
                      <td className="text-right px-4 py-3 font-mono font-semibold">&euro;{tx.amount?.toFixed(2)}</td>
                      <td className="text-center px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          tx.status === 'approved' ? 'bg-green-100 text-green-700' :
                          tx.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          tx.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{tx.status?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {tx.type === 'withdrawal' && tx.bankDetails && (
                          <div>
                            <p className="font-mono">{tx.bankDetails.iban}</p>
                            <p>{tx.bankDetails.accountName}</p>
                          </div>
                        )}
                        {tx.type === 'deposit' && tx.stripeSessionId && <p className="font-mono truncate max-w-[120px]">Stripe: {tx.stripeSessionId.slice(-8)}</p>}
                      </td>
                      <td className="text-right px-4 py-3 text-xs text-gray-500">{tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ''}</td>
                      <td className="text-center px-4 py-3">
                        {tx.status === 'pending_approval' ? (
                          <div className="flex gap-1.5 justify-center">
                            <Button onClick={() => handleWalletTxAction(tx.id, 'approve')} disabled={processingTxId === tx.id} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full px-3 h-7 text-xs" data-testid={`approve-tx-${tx.id}`}>
                              {processingTxId === tx.id ? '...' : 'Approve'}
                            </Button>
                            <Button onClick={() => handleWalletTxAction(tx.id, 'reject')} disabled={processingTxId === tx.id} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-full px-3 h-7 text-xs" data-testid={`reject-tx-${tx.id}`}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">{tx.approvedBy ? `by ${tx.approvedBy}` : tx.rejectedBy ? `by ${tx.rejectedBy}` : '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Registered Users</h2>
              <p className="text-gray-600 mt-1">Total: {users.length} user(s)</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchUsers}
                variant="outline"
                className="border-2 border-gray-300 rounded-full px-6"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-6"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-3 font-semibold text-gray-700">Account #</th>
                  <th className="text-left py-4 px-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left py-4 px-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-4 px-3 font-semibold text-gray-700">Username</th>
                  <th className="text-center py-4 px-3 font-semibold text-gray-700">Status</th>
                  <th className="text-center py-4 px-3 font-semibold text-gray-700">Premium</th>
                  <th className="text-right py-4 px-3 font-semibold text-gray-700">Balance</th>
                  <th className="text-right py-4 px-3 font-semibold text-gray-700">Active Inv.</th>
                  <th className="text-right py-4 px-3 font-semibold text-gray-700">Total Gain</th>
                  <th className="text-center py-4 px-3 font-semibold text-gray-700">Projects</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-3">
                      <div className="font-bold text-[#0d6571]">{u.accountNumber || 'N/A'}</div>
                    </td>
                    <td className="py-4 px-3">
                      <div className="font-medium">{u.firstName} {u.lastName}</div>
                      <div className="text-xs text-gray-500">{new Date(u.dateOfBirth).toLocaleDateString('en-GB')}</div>
                    </td>
                    <td className="py-4 px-3 text-sm">{u.email}</td>
                    <td className="py-4 px-3 font-medium">{u.username}</td>
                    <td className="py-4 px-3 text-center">
                      {u.verified ? (
                        <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <ShieldCheck className="w-3 h-3" />
                          Verified
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <Shield className="w-3 h-3" />
                          Unverified
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-3 text-center">
                      <button
                        data-testid={`toggle-premium-${u.username}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${API}/admin/users/${u.id}/premium`, {
                              isPremium: !u.isPremium
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast({ title: u.isPremium ? 'Premium Disabled' : 'Premium Enabled', description: `${u.firstName} ${u.lastName}` });
                            fetchUsers();
                          } catch {
                            toast({ title: 'Error', description: 'Failed to update premium status', variant: 'destructive' });
                          }
                        }}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          u.isPremium
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        <Crown className="w-3 h-3" />
                        {u.isPremium ? 'Active' : 'Off'}
                      </button>
                    </td>
                    <td className="py-4 px-3">
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData[u.id]?.balance || 0}
                        onChange={(e) => handleFieldChange(u.id, 'balance', e.target.value)}
                        className="text-right font-semibold w-28 ml-auto"
                      />
                    </td>
                    <td className="py-4 px-3">
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData[u.id]?.activeInvestments || 0}
                        onChange={(e) => handleFieldChange(u.id, 'activeInvestments', e.target.value)}
                        className="text-right font-semibold w-28 ml-auto"
                      />
                    </td>
                    <td className="py-4 px-3">
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData[u.id]?.totalGain || 0}
                        onChange={(e) => handleFieldChange(u.id, 'totalGain', e.target.value)}
                        className="text-right font-semibold w-28 ml-auto"
                      />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <Button
                        onClick={() => openProjectModal(u)}
                        size="sm"
                        data-testid={`manage-projects-btn-${u.username}`}
                        className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full px-4"
                      >
                        <FolderOpen className="w-4 h-4 mr-1" />
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No users registered yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Management Modal */}
      {showProjectModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            data-testid="project-modal"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="bg-[#0d6571] text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Projects for {selectedUser.firstName} {selectedUser.lastName}
                </h2>
                <p className="text-white/80 text-sm mt-1">Account #{selectedUser.accountNumber || 'N/A'}</p>
              </div>
              <Button
                onClick={() => setShowProjectModal(false)}
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6">
              {/* Add New Project Form */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#0d6571]" />
                  Add New Project
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                    <Input
                      data-testid="project-name-input"
                      value={projectForm.name}
                      onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                      placeholder="e.g. Website Redesign"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (&euro;)</label>
                    <Input
                      data-testid="project-amount-input"
                      type="number"
                      step="0.01"
                      value={projectForm.amount}
                      onChange={(e) => setProjectForm({ ...projectForm, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <Input
                      data-testid="project-description-input"
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                      placeholder="Brief description of the project"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      data-testid="project-status-select"
                      value={projectForm.status}
                      onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]"
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleAddProject}
                      disabled={savingProject}
                      data-testid="add-project-btn"
                      className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {savingProject ? 'Adding...' : 'Add Project'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Existing Projects List */}
              <h3 className="font-bold text-gray-900 mb-4">Existing Projects</h3>
              {loadingProjects ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0d6571] mx-auto"></div>
                </div>
              ) : userProjects.length > 0 ? (
                <div className="space-y-3">
                  {userProjects.map((project) => (
                    <div
                      key={project.id}
                      data-testid={`admin-project-${project.id}`}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-[#0d6571]/10 p-2 rounded-lg">
                          <FolderOpen className="w-4 h-4 text-[#0d6571]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{project.name}</h4>
                          {project.description && (
                            <p className="text-sm text-gray-500">{project.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(project.createdAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            &euro; {project.amount.toFixed(2)}
                          </p>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                        </div>
                        <Button
                          onClick={() => handleDeleteProject(project.id)}
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-project-${project.id}`}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FolderOpen className="w-12 h-12 mx-auto mb-2" />
                  <p>No projects assigned yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Project Catalog Modal */}
      {showCatalogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div data-testid="catalog-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="bg-amber-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Globe className="w-5 h-5" /> Project Catalog Management
                </h2>
                <p className="text-white/80 text-sm mt-1">Discoverable projects for premium users</p>
              </div>
              <Button onClick={() => { setShowCatalogModal(false); setEditingCatalog(null); }} variant="ghost" size="icon" className="text-white hover:bg-white/20 rounded-full">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              {/* Add/Edit Form */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  {editingCatalog ? <Pencil className="w-5 h-5 text-amber-600" /> : <Plus className="w-5 h-5 text-amber-600" />}
                  {editingCatalog ? 'Edit Project' : 'Add New Project'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                    <Input
                      data-testid="catalog-name-input"
                      value={catalogForm.name}
                      onChange={(e) => setCatalogForm({ ...catalogForm, name: e.target.value })}
                      placeholder="e.g. Green Energy Fund"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ROI % Yearly</label>
                    <Input
                      data-testid="catalog-roi-input"
                      type="number"
                      step="0.1"
                      value={catalogForm.roiYearly}
                      onChange={(e) => setCatalogForm({ ...catalogForm, roiYearly: e.target.value })}
                      placeholder="e.g. 12.5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description / Information</label>
                    <textarea
                      data-testid="catalog-description-input"
                      value={catalogForm.description}
                      onChange={(e) => setCatalogForm({ ...catalogForm, description: e.target.value })}
                      placeholder="Detailed project information..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={catalogForm.published}
                      onChange={(e) => setCatalogForm({ ...catalogForm, published: e.target.checked })}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <label className="text-sm text-gray-700">Published (visible to premium users)</label>
                  </div>
                  <div className="flex items-end gap-2">
                    {editingCatalog && (
                      <Button onClick={() => { setEditingCatalog(null); setCatalogForm({ name: '', description: '', roiYearly: '', published: true }); }} variant="outline" className="rounded-full">
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveCatalog}
                      disabled={savingCatalog}
                      data-testid="save-catalog-btn"
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                    >
                      {savingCatalog ? 'Saving...' : editingCatalog ? 'Update Project' : 'Add Project'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Catalog List */}
              <h3 className="font-bold text-gray-900 mb-4">Published Catalog ({catalogProjects.length})</h3>
              {loadingCatalog ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                </div>
              ) : catalogProjects.length > 0 ? (
                <div className="space-y-3">
                  {catalogProjects.map((p) => (
                    <div key={p.id} data-testid={`catalog-item-${p.id}`} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg ${p.published ? 'bg-amber-100' : 'bg-gray-100'}`}>
                          <Globe className={`w-4 h-4 ${p.published ? 'text-amber-600' : 'text-gray-400'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-gray-900 truncate">{p.name}</h4>
                          {p.description && <p className="text-sm text-gray-500 truncate">{p.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        <div className="text-right">
                          <p className="font-bold text-green-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> {p.roiYearly}%
                          </p>
                          <span className={`text-xs ${p.published ? 'text-green-600' : 'text-gray-400'}`}>
                            {p.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <Button onClick={() => handleEditCatalog(p)} variant="ghost" size="icon" className="rounded-full text-gray-500 hover:text-amber-600">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteCatalog(p.id)} variant="ghost" size="icon" className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Globe className="w-12 h-12 mx-auto mb-2" />
                  <p>No catalog projects yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Applications Modal */}
      {showApplications && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div data-testid="applications-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="bg-[#0d6571] text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" /> Project Applications
                </h2>
                <p className="text-white/80 text-sm mt-1">Users who applied for catalog projects</p>
              </div>
              <Button onClick={() => setShowApplications(false)} variant="ghost" size="icon" className="text-white hover:bg-white/20 rounded-full">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              {applications.length > 0 ? (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <div key={app.id} data-testid={`application-${app.id}`} className="p-4 rounded-xl border border-gray-100 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{app.firstName} {app.lastName}</h4>
                          <p className="text-sm text-gray-500">@{app.username} &middot; {app.userEmail}</p>
                          <p className="text-xs text-gray-400">Account #{app.accountNumber}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#0d6571]">{app.projectName}</p>
                          <p className="text-sm text-green-600 font-semibold">{app.projectRoi}% ROI</p>
                          <p className="text-xs text-gray-400">
                            {new Date(app.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <FolderOpen className="w-12 h-12 mx-auto mb-2" />
                  <p>No applications yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
